'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { IconName } from './icon-registry';
import styles from './icon.module.css';

type IconSize = 'sm' | 'md' | 'lg';

const COLOR_ATTRS = ['fill', 'stroke', 'stop-color'];
const EXCLUDED_VALUES = ['none', 'currentcolor', 'inherit', 'transparent', 'initial', 'unset'];

const iconMarkupCache = new Map<IconName, string | null>();
const iconFetchPromises = new Map<IconName, Promise<string | null>>();

const sizeToClassName: Record<IconSize, keyof typeof styles> = {
  sm: 'iconSm',
  md: 'iconMd',
  lg: 'iconLg',
};

type IconProps = {
  name: IconName;
  size: IconSize;
  color?: string;
  label?: string;
};

function getIconPath(name: IconName) {
  return `/web-static/assets/icons/${name}.svg`;
}

function shouldReplaceColor(value: string) {
  if (!value) {
    return false;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) {
    return false;
  }

  if (EXCLUDED_VALUES.includes(normalizedValue)) {
    return false;
  }

  if (normalizedValue.startsWith('url(') || normalizedValue.startsWith('var(')) {
    return false;
  }

  return true;
}

export function sanitizeSvgColors(svgContent: string): string {
  let updatedContent = svgContent;

  for (const attr of COLOR_ATTRS) {
    const attrRegex = new RegExp(`(${attr})\\s*=\\s*(\"[^\"]*\"|'[^']*')`, 'gi');
    updatedContent = updatedContent.replace(attrRegex, (match, attrName, quotedValue) => {
      const value = quotedValue.slice(1, -1);
      if (!shouldReplaceColor(value)) {
        return match;
      }
      const quote = quotedValue[0];
      return `${attrName}=${quote}currentColor${quote}`;
    });
  }

  const styleRegex = /style\s*=\s*(\"[^\"]*\"|'[^']*')/gi;
  updatedContent = updatedContent.replace(styleRegex, (match, quotedValue) => {
    const quote = quotedValue[0];
    const styleBody = quotedValue.slice(1, -1);
    const declarations = styleBody
      .split(';')
      .map((declaration: string) => declaration.trim())
      .filter(Boolean);

    let changed = false;
    const rewritten = declarations.map((declaration: string) => {
      const [property, value] = declaration.split(':') as [string, string | undefined];
      if (!property || value === undefined) {
        return declaration;
      }

      const trimmedProperty = property.trim();
      const normalizedProperty = trimmedProperty.toLowerCase();
      const trimmedValue = value.trim();

      if (COLOR_ATTRS.includes(normalizedProperty) && shouldReplaceColor(trimmedValue)) {
        changed = true;
        return `${trimmedProperty}:currentColor`;
      }

      return `${trimmedProperty}:${trimmedValue}`;
    });

    if (!changed) {
      return match;
    }

    const newStyle = rewritten.join(';');
    return `style=${quote}${newStyle}${quote}`;
  });

  return updatedContent;
}

export function loadIconMarkup(name: IconName): Promise<string | null> {
  const cachedMarkup = iconMarkupCache.get(name);
  if (cachedMarkup !== undefined) {
    return Promise.resolve(cachedMarkup);
  }

  const inFlight = iconFetchPromises.get(name);
  if (inFlight) {
    return inFlight;
  }

  const loadPromise = (async () => {
    try {
      const response = await fetch(getIconPath(name));
      if (!response.ok) {
        return null as string | null;
      }
      return sanitizeSvgColors(await response.text()) as string | null;
    } catch {
      return null as string | null;
    }
  })().then((nextMarkup) => {
    iconMarkupCache.set(name, nextMarkup);
    return nextMarkup;
  });

  iconFetchPromises.set(name, loadPromise);
  void loadPromise.finally(() => {
    iconFetchPromises.delete(name);
  });
  return loadPromise;
}

export function Icon({ name, size, color, label }: Readonly<IconProps>) {
  // Always null on the first render, never seeded from `iconMarkupCache`.
  //
  // The cache is module-level and shared by every Icon on the page, so by the
  // time a server-rendered subtree hydrates another Icon with the same `name`
  // may already have filled it. Seeding from it then made the first client
  // render emit the <span> while the SSR HTML had nothing (the cache can never
  // be warm on the server, so SSR always renders null) — a hydration mismatch
  // that made React throw away and re-render the whole tree. Because it depends
  // on whether an in-flight icon fetch resolves before hydration, it surfaced
  // intermittently and only on icon-heavy pages.
  //
  // The markup arrives in the effect below instead. A warm cache still resolves
  // without re-fetching, so this costs one microtask before paint, not a
  // network round trip.
  const [markup, setMarkup] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    void loadIconMarkup(name).then((nextMarkup) => {
      if (isActive) {
        setMarkup(nextMarkup);
      }
    });

    return () => {
      isActive = false;
    };
  }, [name]);

  if (!markup) {
    return null;
  }

  const style: CSSProperties = {};
  if (color) {
    style.color = color;
  }

  const ariaProps = label
    ? { 'aria-label': label, role: 'img' as const }
    : { 'aria-hidden': true as const };
  return (
    <span
      aria-hidden={label ? undefined : true}
      className={`${styles.icon} ${styles[sizeToClassName[size]]}`}
      style={style}
      {...ariaProps}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
